<template>
  <event-page :event="event">
    <template v-slot:actions>
      <v-btn
        large
        color="primary"
        depressed
        squared
        :href="bookingHref"
        target="_blank">Booking Form
      </v-btn>
    </template>
    <template v-slot:description>
      <event-section>
        <p>
          Combining inspiring cooking demonstrations and nourishing food with movement, conversations with amazing
          guest speakers and sound healing or creative sessions, these holistic retreats are run seasonally and are
          centred around giving you the tools to connect with your ‘self’, your food and nature to create harmony.
          Be ready to connect, grow and learn in a safe and playful environment!
        </p>
      </event-section>

      <event-section
        :images="retreatsImages"
      >
      </event-section>

      <event-section
        header="Spring Retreat - Revitalise"
        :header-color="headerColor"
        :link="bookingLink"
        :link-text="bookingText"
      >
        <p>
          As Winter draws to a close, Spring is a time for new beginnings and growth. This retreat will help you
          welcome the new season with energy and nourishment.
        </p>
        <p>
          We will connect to our body and mind by moving through Abbie’s lead yoga practice which has
          been especially planned to honour this awakening season. The practice will close with breath work to bring
          back inner peace.
        </p>
        <p>
          Feeling grounded and connected, you will settle with a bespoke infusion while I explain the ways you can
          support and nourish your body in Spring and lead a sprouting cooking demo. You will learn the benefits of
          sprouting and also everything you need to know about creating a minigarden in your kitchen all year round
          with the most alive food on earth!
        </p>
        <p>
          We will then enjoy a delicious and vibrant meal together and celebrate food!
        </p>
        <p>
          The retreat will end with a conversation about open water swimming with our incredible guest
          speaker Heather Schelhase.
        </p>
        <p>
          We can’t wait to welcome you and connect, share and support each other!
        </p>
        <p>Schedule:</p>
        <p class="px-5">
          <ul class="schedule">
            <li>Spring Retreat 21st March 2021</li>
            <li></li>
            <li>10.30: Welcome</li>
            <li>10.45 - 12pm: Yoga with Abbie</li>
            <li>12.05 - 1pm: Cooking demonstration & nutrition lecture with Stephanie</li>
            <li>1.10pm - 1.40pm: Lunch</li>
            <li>1.45pm - 2.30pm: Conversation with Heather and Q&A</li>
          </ul>
        </p>
      </event-section>

      <event-section
        header="The Cooking Demonstration & Nutrition Lecture"
        :header-color="headerColor"
        :images="cookingDemoImages"
      >
        <p>
          The cooking demonstration and nutrition lecture are both aimed to be inspiring and educational. They will
          teach you simple and creative ways to take ownership of your own health and learn how to support and nourish
          yourself in the most delicious ways! They will be interactive and you will have the opportunity to ask
          questions throughout.
        </p>
      </event-section>

      <event-section
        header="The Meal"
        :header-color="headerColor"
        :images="foodImages"
      >
        <p>
          You will enjoy a plant-based lunch prepared by yours truly using whole food organic ingredients and
          focusing on local and sustainable produce, some of which coming from my allotment or foraged. My
          approach to food is vibrant and creative and I use cooking methods which preserve the nutrients while
          bringing out their flavours. <i>Please note that these photos are for illustrative purposes. Actual dishes
          will vary for each retreat to follow the season.</i>
        </p>
      </event-section>

      <event-section
        header="The Yoga"
        :header-color="headerColor"
        :images="yogaImages"
      >
        <p>
          In a beautiful practice synchronising breath and movement into a seamless flow of sequences, Abbie’s
          approach will help to bring awareness on all levels, keeping you totally present.  Be guided through
          beautiful flows with a strong focus on mind-body movement and explore yoga not only in its traditional
          sense. Bringing some heat and energy, the session will finish with a guided meditation or breath work so
          you leave feeling completely in balance. All levels are welcome.
        </p>
      </event-section>

      <event-section
        header="The Conversation"
        :header-color="headerColor"
        :images="guestImages"
      >
        <p>
          This month, we chat to Heather Schelhase about open water swimming. As we dive into Heather’s story with
          swimming, we will discuss the unique power of water and she will explain how to get into it with tips on
          how she makes sure her swimmers get a positive experience and what wonders it does to the body and the
          mind! I had my first swim with Heather in December 2020, it was a cold introduction and one of the
          highlights of my year.
        </p>
      </event-section>

      <event-section
        header="The Location"
        :header-color="headerColor"
        :images="locationImages"
      >
        <p>
          We will connect virtually and we invite you to make yourself a retreat space at home! Stay cosy, create a
          space which opens for connection and where you can find comfort as well as energy. This is your opportunity
          to create a space for our time together and a chance to discover that you can make any place a retreat
          space. The retreat will be a friendly and interactive space where you can ask questions throughout and
          take notes.
        </p>
      </event-section>

      <v-container fluid>
        <v-row align="stretch" justify="center" no-gutters>
          <v-col cols="12" sm="6" md="4" data-aos="fade-up">
            <profile
              :height="profileHeight"
              :image="stephImg"
              name="Stéphanie"
            >
              <template v-slot:description>
                <p>
                  Stéphanie is a Naturopathic Chef and the founder of
                  <a href="https://chefnaturelle.com" target="_blank">Chef Naturelle</a>. She cooks plant-based meals
                  using whole food ingredients designed to nourish from the inside and out. Her educational
                  classes are designed to inspire and empower you to take ownership of your health.
                </p>
              </template>
            </profile>
          </v-col>
          <v-col cols="12" sm="6" md="4" data-aos="fade-up">
            <profile
              :height="profileHeight"
              :image="abbieImg"
              name="Abbie"
            >
              <template v-slot:description>
                <p>
                  Abbie is a Yoga teacher and the founder of
                  <a href="https://www.lotushealthspace.co.uk/" target="_blank">Lotus Health Space</a>. She believes
                  that true health and happiness arises from a collaboration of good food, remaining active and
                  surrounding ourselves with good people doing things we love. She focuses on mind-body movement.
                </p>
              </template>
            </profile>
          </v-col>
          <v-col cols="12" sm="6" md="4" data-aos="fade-up">
            <profile
              :height="profileHeight"
              :image="heatherImg"
              name="Heather"
            >
              <template v-slot:description>
                <p>
                  Heather is a qualified open water coach and the founder of
                  <a href="https://www.pool2lake.uk/" target="_blank">Pool2Lake</a>. Having always loved swimming,
                  she was inspired by friends to do open water swimming and is now coaching people. She also trained
                  with Keri-anne Payne, two-time 10km open water world champion and Olympic silver medallist!
                </p>
              </template>
            </profile>
          </v-col>
        </v-row>
      </v-container>

      <event-section
        header="Bookings"
        :header-color="headerColor"
        :link="bookingLink"
        :link-text="bookingText"
      >
        <p>
          <b>*Introductory offer for our first retreat*</b>
        </p>

        <p>- Please note that bookings close on 16th March evening -</p>

        <p>
          Standard Investment <br/>
          <span class="subtext">(for those who can and want to contribute fully)</span>
          <br/>
          £50
          <br/>
          £35 without lunch
          <br/>
        </p>

        <p>
          Reduced Investment <br/>
          <span class="subtext">(for those who are having a difficult time*)</span>
          <br/>
          £35
          <br/>
          £25 without lunch
          <br/>
          * I understand that this may be a very challenging time for you and would like to give you the opportunity
          to attend. If those prices are still unaffordable for you, please do
          <contact :subject="event.title" text="get in touch"></contact>.
        </p>

        <p>
          Retreat Package:
          <ul>
            <li>75mins yoga, pranayama and meditation practice</li>
            <li>Cooking Demonstration and Nutrition Lecture</li>
            <li>Lunch and handmade infusion (when applicable)</li>
            <li>2 exclusive recipes</li>
            <li>Discussion with special guest</li>
            <li>Live Q&A</li>
          </ul>
        </p>
      </event-section>

      <event-section
        header="How It Works"
        :header-color="headerColor"
        :link="bookingLink"
        :link-text="bookingText"
      >
        <p>
          This retreat will take place online via GoogleMeet and we recommend you use a laptop or computer
          (instead of a phone) to join the retreat. You do not need to download any software or hold a Gmail account,
          you will simply receive a link to join the retreat the day before.
        </p>
        <p>
          For the lunch option, your delicious meal will be delivered to your home the day before by
          yours truly. Please note that delivery is only available within 10 miles from Hertford and you will be
          contacted beforehand with a delivery slot. Collection can be arranged upon request (please do so before
          booking your ticket). Whichever ticket you purchase, you will receive a PDF with the recipes before the
          event so that those outside delivery area can cook and enjoy the same meal and all can re-create
          the meals at home too!
        </p>
        <p>
          Free of refined sugars and flours, the meal is prepared fresh in my kitchen using a variety of whole foods
          and are therefore not suitable for anyone suffering from severe food allergies. If there is any doubt,
          please <contact :subject="event.title" text="get in touch"></contact>.
        </p>
      </event-section>

      <event-section>
        <p class="subtext text-center">Photos of Cooking Demo and Meal by
          <a href="https://www.eatbreathecreate.photography/" target="_blank">Nat Aubry</a>
        </p>
      </event-section>

    </template>
  </event-page>
</template>

<script>
import Contact from '@/components/Contact'
import EventPage from '@/components/EventPage'
import EventSection from '@/components/EventSection'
import Profile from '@/components/Profile'
import {
  VBtn,
  VContainer,
  VCol,
  VRow,
} from 'vuetify/lib'

export default {
  components: {
    Contact,
    EventPage,
    EventSection,
    Profile,
    VBtn,
    VContainer,
    VCol,
    VRow,
  },
  computed: {
    meta() {
      return {
        'description': 'Spring Holistic Retreat - 21st March 2021',
        'image': require('@/assets/images/retreats/spring/retreats.png?vuetify-preload'),
        'title': this.event.title,
      }
    },
    profileHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'sm': return '410'
        case 'md': return '350'
        case 'lg': return '300'
      }
      return undefined
    }
  },
  data: () => ({
    cookingDemoImages: [
      {src: require('@/assets/images/retreats/spring/steph.jpg?vuetify-preload'), position: 'center top'},
      {src: require('@/assets/images/retreats/spring/cooking-demo-12.jpg?vuetify-preload'), position: 'center bottom'},
    ],
    foodImages: [
      {src: require('@/assets/images/retreats/spring/takeaway-1.jpg?vuetify-preload'), position: '-48px center'},
      {src: require('@/assets/images/retreats/spring/food-6.jpg?vuetify-preload')},
    ],
    guestImages: [
      {src: require('@/assets/images/retreats/spring/heather-1.jpg?vuetify-preload')},
      {src: require('@/assets/images/retreats/spring/heather-2.jpg?vuetify-preload')},
    ],
    locationImages: [
      {src: require('@/assets/images/retreats/spring/location-1.jpg?vuetify-preload'), position: 'center bottom'},
      {src: require('@/assets/images/retreats/spring/location-2.jpg?vuetify-preload')},
    ],
    retreatsImages: [
      {src: require('@/assets/images/retreats/spring/retreats.png?vuetify-preload')},
    ],
    yogaImages: [
      {src: require('@/assets/images/retreats/spring/abbie-1.jpg?vuetify-preload')},
      {src: require('@/assets/images/retreats/spring/abbie-2.jpg?vuetify-preload')},
    ],
    abbieImg: {url: require('@/assets/images/retreats/spring/abbie-profile.jpg?vuetify-preload'), position: 'center top'},
    heatherImg: {url: require('@/assets/images/retreats/spring/heather-1.jpg?vuetify-preload')},
    stephImg: {url: require('@/assets/images/retreats/spring/steph-profile.jpg?vuetify-preload')},
    bookingLink: 'https://docs.google.com/forms/d/e/1FAIpQLSeL0xtzMZBFt2E6R4WCiKlqMehy4gotjf4lbaj0tEdnnmLjDg/viewform',
    bookingText: 'Book Your Space',
    headerColor: '#929F6F',
    event: {
      image: {
        url: require('@/assets/images/retreats/spring/spring.jpg')
      },
      sections: [],
      subtitles: ['21st March 2021', '10:30am - 2:30pm'],
      title: 'Spring Holistic Retreat',
    }
  })
}
</script>

<style lang="scss" scoped>
h1 {
  color: blue;
  font-family: 'Karla', sans-serif;
  font-weight: bold;
  text-transform: none;
  font-size: 40px;
  text-align: center;
}

h3 {
  font-size: 30px;
  text-transform: uppercase;
  padding-top: 20px;
}

ul {
  padding-left: 35px;
  padding-right: 35px;
}

.pricing-details {
  color: #697b5e;
  border-bottom: 1px solid #697b5e;
  margin-bottom: 23px !important;
  padding-bottom: 20px;
  text-transform: uppercase;
  font-weight: bold;
}

.schedule {
  list-style: none;
  padding-left: 0;
}

.small-print {
  display: block;
  margin-top: 10px;
  font-style: italic;
  font-size: 13px;
}

.subtext {
  font-style: italic;
}
</style>
